import { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  Grid,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import { Frame } from '../../components/layouts/Frame';
import { useAPI } from '../../hooks/useAPI';
import { useAuth } from '../../contexts/AuthContext';

export const User = () => {
  const { user, isAuthenticated, signOut } = useAuth();
  const { fetchData } = useAPI();

  const [formData, setFormData] = useState({
    firstName: user?.first_name ?? '',
    lastName: user?.last_name ?? '',
    company: user?.company_name ?? '',
  });

  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });

  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [passwordError, setPasswordError] = useState('');
  const [currentPasswordError, setCurrentPasswordError] = useState('');

  const [open, setOpen] = useState(false);

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  useEffect(() => {
    if (isAuthenticated && user) {
      setFormData({
        firstName: user.first_name ?? '',
        lastName: user.last_name ?? '',
        company: user.company_name ?? '',
      });
    }
  }, [isAuthenticated, user]);

  const handleChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setFormErrors(prev => ({ ...prev, [field]: '' }));
  };

  const handlePasswordChange = (field: string, value: string) => {
    setPasswordData(prev => ({ ...prev, [field]: value }));
    if (field === 'newPassword' || field === 'confirmPassword') {
      setPasswordError('');
    }
  };

  const validateForm = () => {
    const errors: Record<string, string> = {};

    if (!formData.firstName.trim()) errors.firstName = 'First name is required';
    if (!formData.lastName.trim()) errors.lastName = 'Last name is required';

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleUpdate = async () => {
    if (!validateForm()) return;

    const payload = {
      first_name: formData.firstName,
      last_name: formData.lastName,
      company_name: formData.company,
    };

    const { data } = await fetchData(`/users/${user?.id}`, payload, 'POST');
  };

  const handleDelete = () => {
    const payload = {
      first_name: '',
      last_name: '',
      company_name: '',
      email: '',
    };

    fetchData(`/users/${user?.id}`, payload, 'POST').then(() => {
      signOut();
    });
  };

  const handlePasswordUpdate = async () => {
    if (!passwordData.currentPassword) {
      setCurrentPasswordError('Please enter your password.');
      return;
    }

    if (
      !passwordData.currentPassword ||
      !passwordData.newPassword ||
      !passwordData.confirmPassword
    ) {
      setPasswordError('All password fields are required.');
      return;
    }

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setPasswordError('New passwords do not match.');
      return;
    }

    const payload = {
      user_id: user?.id,
      current_password: passwordData.currentPassword,
      password: passwordData.newPassword,
    };

    const { data } = await fetchData('/users/update_password', payload, 'POST');

    if (data?.error) {
      setCurrentPasswordError(data?.msg ?? '');
      return;
    }

    setPasswordError('');
    setCurrentPasswordError('');
  };

  return (
    <Frame title="User">
      <Box sx={{ mx: 'auto', p: 2 }}>
        <Dialog
          open={open}
          onClose={handleClose}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description">
          <DialogTitle id="alert-dialog-title">{'Warning'}</DialogTitle>
          <DialogContent>
            <DialogContentText id="alert-dialog-description">
              Are you sure yuo would like to delete your account?
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button variant="contained" onClick={handleClose}>
              No
            </Button>
            <Button onClick={handleDelete} autoFocus>
              Yes
            </Button>
          </DialogActions>
        </Dialog>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6}>
            <Typography variant="h6" fontWeight="600" sx={{ mb: 1 }}>
              First Name
            </Typography>
            <TextField
              fullWidth
              variant="outlined"
              value={formData.firstName}
              onChange={e => handleChange('firstName', e.target.value)}
              error={!!formErrors.firstName}
              helperText={formErrors.firstName}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <Typography variant="h6" fontWeight="600" sx={{ mb: 1 }}>
              Last Name
            </Typography>
            <TextField
              fullWidth
              variant="outlined"
              value={formData.lastName}
              onChange={e => handleChange('lastName', e.target.value)}
              error={!!formErrors.lastName}
              helperText={formErrors.lastName}
            />
          </Grid>

          <Grid item xs={12}>
            <Typography variant="h6" fontWeight="600" sx={{ mb: 1 }}>
              Company Name
            </Typography>
            <TextField
              fullWidth
              variant="outlined"
              value={formData.company}
              onChange={e => handleChange('company', e.target.value)}
            />
          </Grid>

          <Grid item xs={12}>
            <Typography variant="h6" fontWeight="600" sx={{ mb: 1 }}>
              Email
            </Typography>
            <TextField
              fullWidth
              variant="outlined"
              value={user?.email ?? ''}
              disabled
            />
          </Grid>

          <Grid item xs={12}>
            <Stack direction="row" spacing={2} justifyContent="flex-end">
              <Button
                variant="contained"
                color="primary"
                onClick={handleUpdate}>
                Update
              </Button>
              <Button
                variant="outlined"
                color="error"
                onClick={handleClickOpen}>
                Delete account
              </Button>
            </Stack>
          </Grid>

          <Grid item xs={12}>
            <Typography variant="h5" fontWeight="700" sx={{ mt: 4, mb: 2 }}>
              Update Password
            </Typography>
          </Grid>

          <Grid item xs={12} sx={{ mb: 1 }}>
            <TextField
              fullWidth
              type="password"
              label="Current Password"
              variant="outlined"
              value={passwordData.currentPassword}
              onChange={e =>
                handlePasswordChange('currentPassword', e.target.value)
              }
              error={!!currentPasswordError}
              helperText={currentPasswordError}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              type="password"
              label="New Password"
              variant="outlined"
              value={passwordData.newPassword}
              onChange={e =>
                handlePasswordChange('newPassword', e.target.value)
              }
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              type="password"
              label="Confirm New Password"
              variant="outlined"
              value={passwordData.confirmPassword}
              onChange={e =>
                handlePasswordChange('confirmPassword', e.target.value)
              }
              error={!!passwordError}
              helperText={passwordError}
            />
          </Grid>

          <Grid item xs={12}>
            <Stack direction="row" justifyContent="flex-end">
              <Button
                variant="contained"
                color="secondary"
                onClick={handlePasswordUpdate}>
                Update Password
              </Button>
            </Stack>
          </Grid>
        </Grid>
      </Box>
    </Frame>
  );
};
